import { computed, effect, signal, type Injector } from '@angular/core'
import type * as ents from '@attain-apps/entities'
import { TypeAsserter } from '@attain-apps/type-guards'

import * as conversation from './conversation'
import * as messages from './messages'
import * as workspace from './workspace'

/**
 * A wrapper that groups multiple assistants together. Allows managing related
 * assistants as a single unit
 */
export class Group {
  /**
   * Unique identifier for the group
   */
  public get id(): string {
    return this._params.id
  }

  /**
   * Gets the default agent ID for the group
   *
   * NOTE: - If no default is specified, the first assistant display's agent ID is used
   */
  public get defaultAgentId(): ents.core.agent.Agent.Id {
    return (
      this._params.defaultAgentId ?? this._params.assistantDisplays[0].id
    )
  }

  /**
   * Ordered list of agent IDs in this group
   */
  public readonly agentIds: readonly ents.core.agent.Agent.Id[]

  /**
   * Map of agent IDs to their respective assistant displays.
   */
  protected readonly assistantDisplaysByAgentId: Map<
    ents.core.agent.Agent.Id,
    ents.core.agent.Display
  >

  /** {@link conversation.Collection} for the group */
  public readonly conversation: conversation.Collection

  /** @see {@link workspace.Collection} */
  public readonly workspace: workspace.Collection

  /**
   * {@link messages.Collection}
   */
  public readonly messages: messages.Collection

  /**
   * Currently selected agent ID in the group
   */
  public readonly selectedAgentIdSg = signal(this.defaultAgentId)

  /**
   * Currently selected assistant display in the group
   */
  public readonly selectedAssistantDisplaySg = computed(() =>
    TypeAsserter.validate(
      this.assistantDisplaysByAgentId.get(this.selectedAgentIdSg()),
      TypeAsserter.assertIsNotNullish,
    ),
  )

  /**
   * Get global conversations across all assistant displays in the group.
   */
  public readonly globalConversationGroup

  /**
   * Gets conversations for a given workspace ID
   */
  public readonly getWorkspaceGroup

  /**
   * Get conversations for a given agent ID
   */
  public readonly getAgentGroup

  /**
   * Effect to update the selected agent ID when current conversation changes.
   */
  public readonly setCurrentConversationAgentId = effect(() => {
    const currentConversation =
      this.conversation.plugins.currentEntityPlugin.feature.selectors
        .getEntity()
        .asSignal()
    if (currentConversation) {
      this.selectedAgentIdSg.set(currentConversation.agentId)
    }
  }, {
    injector: this._params.injector,
  })

  constructor(private readonly _params: Group.constructor.Params) {
    this.assistantDisplaysByAgentId = new Map(
      this._params.assistantDisplays.map(assistantDisplay => [
        assistantDisplay.id,
        assistantDisplay,
      ]),
    )
    this.agentIds = Array.from(this.assistantDisplaysByAgentId.keys())
    this.workspace = new workspace.Collection(this)
    this.conversation = new conversation.Collection(this)
    this.messages = new messages.Collection(this)

    this.globalConversationGroup =
      this.conversation.plugins.groupPlugin.feature.selectors.getGroupWithEntities(
        conversation.GLOBAL_WORKSPACE_ID,
      )

    this.getAgentGroup = this.conversation.feature.createSelectorMethod(
      (id: ents.core.agent.Agent.Id) =>
        this.conversation.plugins.groupPlugin.feature.selectors.getGroupWithEntities(
          id,
        ),
    )

    this.getWorkspaceGroup = this.conversation.feature.createSelectorMethod(
      (id: ents.core.agent.Workspace.Id) =>
        this.conversation.plugins.groupPlugin.feature.selectors.getGroupWithEntities(
          id,
        ),
    )
  }

  /**
   * Get an assistant display by agent ID
   */
  public getAssistantDisplayByAgentId(
    agentId: ents.core.agent.Agent.Id,
  ): ents.core.agent.Display {
    const assistantDisplay = this.assistantDisplaysByAgentId.get(agentId)
    TypeAsserter.assertIsNotNullish(assistantDisplay)
    return assistantDisplay
  }

  /**
   * Get all assistant displays in this group
   */
  public getAssistantDisplays(): ents.core.agent.Display[] {
    return Array.from(this.assistantDisplaysByAgentId.values())
  }

  /**
   * Check if this group contains the given agent ID
   */
  public hasAgent(agentId: ents.core.agent.Agent.Id): boolean {
    return this.assistantDisplaysByAgentId.has(agentId)
  }
}

export namespace Group {
  export namespace constructor {
    export interface Params {
      /** @see {@link Group.id} */
      id: string
      /**
       * Array of assistant displays to include in this group
       *
       * NOTE: Must contain at least one assistant display.
       */
      readonly assistantDisplays: readonly [
        ents.core.agent.Display,
        ...ents.core.agent.Display[],
      ]

      /** Default agent ID for the group */
      readonly defaultAgentId?: ents.core.agent.Agent.Id

      /** Injector used for effects on manually created groups. */
      readonly injector: Injector
    }
  }
}
